import * as React from "react";
import {
  HoldingsInformation,
  ResourceDWR,
  TextualHolding,
} from "../../../dto/SuggestionResult";
import { useLanguage } from "../../Language";
import { SerialHoldings } from "./SerialHoldings";
import { NonSerialHoldings } from "./NonSerialHoldings";
import { TextualHoldings } from "./TextualHoldings";
import { useLocalHoldingsBranch } from "./LocalHoldingsBranch";

interface BranchCopies {
  branchIdentifier: string;
  branchName: string;
  holdings: HoldingsInformation[];
  textualHoldings: TextualHolding[];
  serialHoldings: HoldingsInformation[];
}

export interface AllCopiesProps {
  resource: ResourceDWR;
}
export const AllCopies: React.FC<AllCopiesProps> = ({ resource }) => {
  const { translate } = useLanguage();
  const { branchIdentifier: localHoldingsBranchId } = useLocalHoldingsBranch();

  // Group by branch
  const branchCopies = [
    ...resource.holdingsInformations,
    ...resource.textualHoldings,
  ].reduce((acc, holding) => {
    const { branchIdentifier, branchName } = holding;

    if (!acc[branchIdentifier]) {
      acc[branchIdentifier] = {
        branchIdentifier,
        branchName,
        holdings: [],
        textualHoldings: [],
        serialHoldings: [],
      };
    }

    if ("textualHoldingStatement" in holding) {
      acc[branchIdentifier].textualHoldings.push(holding);
    } else if (holding.serialItem) {
      acc[branchIdentifier].serialHoldings.push(holding);
    } else {
      acc[branchIdentifier].holdings.push(holding);
    }

    return acc;
  }, {} as Record<string, BranchCopies>);

  // Display local copies first then sort by branch name
  const sortedBranchCopies = Object.values(branchCopies).sort((a, b) =>
    a.branchIdentifier === localHoldingsBranchId
      ? -1
      : b.branchIdentifier === localHoldingsBranchId
      ? 1
      : a.branchName.localeCompare(b.branchName)
  );

  return (
    <>
      {sortedBranchCopies.map((branch) => (
        <div
          style={{ marginBottom: 50, fontSize: "0.95em" }}
          key={branch.branchIdentifier}
        >
          <h3 style={{ fontSize: 16 }}>
            {translate("findOnShelf")} {translate("availability_at")}{" "}
            {branch.branchName}
          </h3>
          {branch.textualHoldings.length > 0 && (
            <TextualHoldings holdings={branch.textualHoldings} />
          )}
          {branch.holdings.length > 0 && (
            <NonSerialHoldings resource={resource} holdings={branch.holdings} />
          )}
          {branch.serialHoldings.length > 0 && (
            <SerialHoldings
              resource={resource}
              holdings={branch.serialHoldings}
            />
          )}
        </div>
      ))}
      {sortedBranchCopies.length === 0 && (
        <p>
          {resource.downloadable
            ? translate("downloadableResourceMessage")
            : translate("noCopies")}
        </p>
      )}
    </>
  );
};
